import { memo, type ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { type Board } from '@air/api/types';
import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';

import { useWorkspaceSidebarBoardDropdownOptions } from '~/components/v2/hooks/useWorkspaceSidebarBoardDropdownOptions';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';

const SIDEBAR_BOARD_CONTEXT_MENU_LOCK_TOKEN_PREFIX = 'sidebar-board-context-menu';

export type WorkspaceSidebarBoardsListItemContextMenuProps = {
  board: Board;
  children: ReactElement;
  onOpenChange: (open: boolean) => void;
  onRenamingStart: () => void;
};

export const WorkspaceSidebarBoardsListItemContextMenu = memo(
  ({ board, children, onOpenChange, onRenamingStart }: WorkspaceSidebarBoardsListItemContextMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const skipCloseAutoFocusRef = useRef(false);
    const { trackSidebarInteraction } = useTrackSidebarInteraction();
    const { handleOpenChange: handleLockChange } = useWorkspaceSidebarPushLock({
      token: `${SIDEBAR_BOARD_CONTEXT_MENU_LOCK_TOKEN_PREFIX}-${board.id}`,
    });
    const handleRenamingStart = useCallback(() => {
      skipCloseAutoFocusRef.current = true;
      onRenamingStart();
    }, [onRenamingStart]);
    const { workspaceSidebarBoardDropdownOptions } = useWorkspaceSidebarBoardDropdownOptions({
      board,
      open: isOpen,
      onRename: handleRenamingStart,
    });

    const shouldShowMenu = useMemo(
      () => workspaceSidebarBoardDropdownOptions.some((option) => option.type !== 'separator'),
      [workspaceSidebarBoardDropdownOptions],
    );

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setIsOpen(open);
        handleLockChange(open);
        onOpenChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'board',
          });
        }
      },
      [handleLockChange, onOpenChange, trackSidebarInteraction],
    );

    const handleCloseAutoFocus = useCallback((event: Event) => {
      if (!skipCloseAutoFocusRef.current) {
        return;
      }

      event.preventDefault();
      skipCloseAutoFocusRef.current = false;
    }, []);

    if (!shouldShowMenu) {
      return children;
    }

    return (
      <ContextMenu
        aria-label={`${board.title} actions`}
        onCloseAutoFocus={handleCloseAutoFocus}
        onOpenChange={handleOpenChange}
        trigger={<div className="w-full min-w-0">{children}</div>}
      >
        {renderContextMenuItems({ options: workspaceSidebarBoardDropdownOptions })}
      </ContextMenu>
    );
  },
);

WorkspaceSidebarBoardsListItemContextMenu.displayName = 'WorkspaceSidebarBoardsListItemContextMenu';
